import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApiBaseUrl, API_TIMEOUT } from '../config/apiConfig';

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add authentication token and impersonation headers
apiClient.interceptors.request.use(
  (config) => {
    // Set baseURL dynamically on each request to handle ngrok/localhost detection
    if (!config.baseURL) {
      config.baseURL = getApiBaseUrl();
    }
    
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add impersonation headers if present
    const impersonatingUserId = localStorage.getItem('impersonating_user_id');
    const adminId = localStorage.getItem('impersonation_admin_id');
    
    if (impersonatingUserId && adminId) {
      config.headers['X-Impersonating-User-Id'] = impersonatingUserId;
      config.headers['X-Admin-Id'] = adminId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful API calls in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    // SECURITY FIX #20: Only log detailed errors in development mode
    const requestId = error.response?.headers?.['x-request-id'];
    const errorDetails = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      requestId,
      timestamp: new Date().toISOString(),
    };

    // Only log full error details in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', JSON.stringify(errorDetails, null, 2));
      // Also log the full response data if available
      if (error.response?.data) {
        console.error('API Error Response Data:', JSON.stringify(error.response.data, null, 2));
      }
    } else {
      // In production, only log minimal information
      console.error('API Error:', JSON.stringify({
        status: error.response?.status,
        requestId,
        timestamp: errorDetails.timestamp
      }, null, 2));
    }

    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('session_id');
      
      // Only redirect if we're on a protected route that requires authentication
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/services', '/about', '/contact'];
      const isPublicPath = publicPaths.some(path => currentPath === path || currentPath.startsWith(path));
      
      // Only redirect to login if we're not on a public page and not already on login page
      if (!isPublicPath && !currentPath.includes('/login')) {
        console.log('401 error on protected route, redirecting to login');
        window.location.href = '/login';
      } else {
        console.log('401 error on public route, not redirecting');
      }
    }
    
    // Return enhanced error object
    return Promise.reject({
      ...error,
      requestId,
      timestamp: errorDetails.timestamp,
    });
  }
);

// Generic API service methods
export class ApiService {
  static async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.get<T>(url, config);
    return response.data;
  }

  static async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.post<T>(url, data, config);
    return response.data;
  }

  static async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.put<T>(url, data, config);
    return response.data;
  }

  static async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.delete<T>(url, config);
    return response.data;
  }

  static async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.patch<T>(url, data, config);
    return response.data;
  }
}

// Backend API response interface
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: Record<string, string[]>;
}

// Authentication service
export class AuthService {
  static async login(email: string, password: string) {
    const response = await ApiService.post<ApiResponse<{
      token: string;
      token_type: string;
      expires_in: number;
      user: any;
      session_id: string;
    }>>('/auth/login', { email, password });
    
    if (!response.success) {
      throw new Error(response.message || 'Login failed');
    }
    
    // Store token and user data
    localStorage.setItem('auth_token', response.data.token);
    localStorage.setItem('user_data', JSON.stringify(response.data.user));
    localStorage.setItem('session_id', response.data.session_id);
    
    return response.data;
  }

  static async register(userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    role?: string;
  }) {
    const response = await ApiService.post<ApiResponse<{
      token: string;
      token_type: string;
      expires_in: number;
      user: any;
      session_id: string;
    }>>('/auth/register', userData);
    
    if (!response.success) {
      throw new Error(response.message || 'Registration failed');
    }
    
    // Store token and user data
    localStorage.setItem('auth_token', response.data.token);
    localStorage.setItem('user_data', JSON.stringify(response.data.user));
    localStorage.setItem('session_id', response.data.session_id);
    
    return response.data;
  }

  static async logout() {
    try {
      await ApiService.post<ApiResponse<void>>('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call success
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('session_id');
    }
  }

  static async refreshToken() {
    const response = await ApiService.post<ApiResponse<{
      token: string;
      token_type: string;
      expires_in: number;
    }>>('/auth/refresh');
    
    if (!response.success) {
      throw new Error(response.message || 'Token refresh failed');
    }
    
    localStorage.setItem('auth_token', response.data.token);
    return response.data;
  }

  static async getProfile() {
    const response = await ApiService.get<ApiResponse<{
      user: any;
      permissions: string[];
      active_sessions: number;
    }>>('/auth/profile');
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to get profile');
    }
    
    return response.data;
  }

  static async changePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
    const response = await ApiService.post<ApiResponse<void>>('/auth/change-password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: confirmPassword
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Password change failed');
    }
    
    return response;
  }

  static getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  static getUser(): any | null {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

// User interfaces
export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  phone?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  role: string;
  phone?: string;
  is_active: boolean;
}

// Users service
export class UsersService {
  static async getUsers(params?: { 
    role?: string; 
    page?: number; 
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<{
    users: User[];
    total: number;
    current_page: number;
    last_page: number;
  }> {
    const response = await ApiService.get<{
      success: boolean;
      users: {
        data: User[];
        total: number;
        current_page: number;
        last_page: number;
      };
    }>('/admin/users', { params });
    
    return {
      users: response.users.data,
      total: response.users.total,
      current_page: response.users.current_page,
      last_page: response.users.last_page
    };
  }

  static async getUser(id: number): Promise<User> {
    const response = await ApiService.get<{
      success: boolean;
      user: User;
    }>(`/admin/users/${id}`);
    
    return response.user;
  }

  static async createUser(data: CreateUserData): Promise<User> {
    const response = await ApiService.post<{
      success: boolean;
      user: User;
      message: string;
    }>('/admin/staff', data);
    
    return response.user;
  }

  static async updateUser(id: number, data: Partial<CreateUserData>): Promise<User> {
    const response = await ApiService.put<{
      success: boolean;
      user: User;
      message: string;
    }>(`/admin/users/${id}`, data);
    
    return response.user;
  }

  static async deleteUser(id: number): Promise<void> {
    await ApiService.delete<{
      success: boolean;
      message: string;
    }>(`/admin/users/${id}`);
  }
}

// Projects service
export class ProjectsService {
  static async getProjects(params?: { 
    status?: string; 
    client_id?: number; 
    page?: number; 
    limit?: number;
  }) {
    return ApiService.get<any>('/projects', { params });
  }

  static async getProject(id: number) {
    return ApiService.get<any>(`/projects/${id}`);
  }

  static async createProject(data: any) {
    return ApiService.post<any>('/projects', data);
  }

  static async updateProject(id: number, data: any) {
    return ApiService.put<any>(`/projects/${id}`, data);
  }

  static async deleteProject(id: number) {
    return ApiService.delete<any>(`/projects/${id}`);
  }

  static async assignDeveloper(projectId: number, developerId: number, data: any) {
    return ApiService.post<any>(`/projects/${projectId}/assign/${developerId}`, data);
  }

  static async unassignDeveloper(projectId: number, developerId: number) {
    return ApiService.delete<any>(`/projects/${projectId}/unassign/${developerId}`);
  }
}

// Invoice interfaces
export interface Invoice {
  id: number;
  invoiceNumber: string;
  clientId: number;
  clientName?: string;
  projectId?: number;
  projectName?: string;
  amount: number;
  balanceDue: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  issueDate: string;
  dueDate?: string;
  paidDate?: string;
  description?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateInvoiceData {
  invoice_number?: string;
  client_id: number;
  project_id?: number;
  amount: number;
  status?: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  issue_date?: string;
  due_date?: string;
  paid_date?: string;
  description?: string;
  notes?: string;
  metadata?: any;
}

export interface FinancialStats {
  totalRevenue: number;
  monthlyRevenue: number;
  pendingInvoices: number;
  overdueInvoices: number;
  outstandingAmount: number;
  lastUpdated: string;
}

export interface UnbilledHoursItem {
  projectId: number;
  projectName: string;
  taskId: number;
  taskTitle: string;
  taskType: string;
  hours: number;
  hourlyRate: number;
  amount: number;
  timeLogs: Array<{
    id: number;
    user: string;
    hours: number;
    date: string;
    description?: string;
  }>;
}

export interface UnbilledHoursData {
  clientId: number;
  clientName: string;
  totalHours: number;
  totalAmount: number;
  unbilledItems: UnbilledHoursItem[];
  projectCount: number;
  itemCount: number;
}

// Invoices service
export class InvoicesService {
  static async getInvoices(params?: {
    status?: string;
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<{
    invoices: Invoice[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      last_page: number;
    };
  }> {
    const response = await ApiService.get<{
      success: boolean;
      data: {
        invoices: Invoice[];
        pagination: {
          current_page: number;
          per_page: number;
          total: number;
          last_page: number;
        };
      };
    }>('/admin/financial/invoices', { params });
    
    return response.data;
  }

  static async getInvoice(id: number): Promise<{
    invoice: Invoice;
    payments: any[];
  }> {
    const response = await ApiService.get<{
      success: boolean;
      data: {
        invoice: Invoice;
        payments: any[];
      };
    }>(`/admin/financial/invoices/${id}`);
    
    return response.data;
  }

  static async createInvoice(data: CreateInvoiceData): Promise<Invoice> {
    const response = await ApiService.post<{
      success: boolean;
      message: string;
      data: Invoice;
    }>('/admin/financial/invoices', data);
    
    return response.data;
  }

  static async updateInvoice(id: number, data: Partial<CreateInvoiceData>): Promise<Invoice> {
    const response = await ApiService.put<{
      success: boolean;
      message: string;
      data: Invoice;
    }>(`/admin/financial/invoices/${id}`, data);
    
    return response.data;
  }

  static async deleteInvoice(id: number): Promise<void> {
    await ApiService.delete<{
      success: boolean;
      message: string;
    }>(`/admin/financial/invoices/${id}`);
  }

  static async getStats(): Promise<FinancialStats> {
    const response = await ApiService.get<{
      success: boolean;
      data: FinancialStats;
    }>('/admin/financial/stats');
    
    return response.data;
  }

  static async getUnbilledHours(clientId: number): Promise<UnbilledHoursData> {
    const response = await ApiService.get<{
      success: boolean;
      data: UnbilledHoursData;
    }>(`/admin/financial/clients/${clientId}/unbilled-hours`);
    
    return response.data;
  }
}

// Client interfaces
export interface Client {
  id: number;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  industry?: string;
  size?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  status: 'active' | 'inactive' | 'suspended' | 'archived';
  created_at: string;
  updated_at: string;
}

// Clients service
export class ClientsService {
  static async getClients(params?: {
    status?: string;
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<{
    clients: Client[];
    pagination?: {
      current_page: number;
      per_page: number;
      total: number;
      last_page: number;
    };
  }> {
    const response = await ApiService.get<{
      success: boolean;
      data: {
        clients: Client[];
        pagination?: {
          current_page: number;
          per_page: number;
          total: number;
          last_page: number;
        };
      };
    }>('/admin/clients', { params });
    
    return response.data;
  }

  static async getClient(id: number): Promise<Client> {
    const response = await ApiService.get<{
      success: boolean;
      data: Client;
    }>(`/admin/clients/${id}`);
    
    return response.data;
  }
}

export default apiClient;